import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { format } from "date-fns"
import { de } from "date-fns/locale"
import { Calendar } from "@/components/ui/calendar"
import { cn } from "@/lib/utils"

const monthAt = (year, month = 0) => {
  const date = new Date(0)
  date.setFullYear(year, month, 1)
  date.setHours(12, 0, 0, 0)
  return date
}

// Retain DayPicker's accessible caption while navigation is rendered above it.
function HiddenCaption({ id, displayMonth }) {
  return <span id={id} className="sr-only">{format(displayMonth, "LLLL yyyy", { locale: de })}</span>
}
const dayComponents = { Caption: HiddenCaption }

export function DateSelectionCalendar({ selected, onSelect, minDate, maxDate, disabled }) {
  const clampMonth = date => {
    const bounded = minDate && date < minDate ? minDate : maxDate && date > maxDate ? maxDate : date
    return monthAt(bounded.getFullYear(), bounded.getMonth())
  }
  const [month, setMonth] = React.useState(() => clampMonth(selected || new Date()))
  const [view, setView] = React.useState("days")
  const [yearStart, setYearStart] = React.useState(() => Math.max(1, month.getFullYear() - 9))
  const panelRef = React.useRef(null)
  const titleRef = React.useRef(null)
  const previousView = React.useRef(view)
  const selectedTime = selected?.getTime()
  const minTime = minDate?.getTime()
  const maxTime = maxDate?.getTime()

  React.useEffect(() => {
    if (selectedTime !== undefined) {
      const bounded = new Date(Math.max(minTime ?? -Infinity, Math.min(maxTime ?? Infinity, selectedTime)))
      setMonth(monthAt(bounded.getFullYear(), bounded.getMonth()))
    }
  }, [selectedTime, minTime, maxTime])

  React.useEffect(() => {
    if (previousView.current !== view) {
      const target = view === "days" ? titleRef.current :
        (panelRef.current?.querySelector('button[aria-pressed="true"]:not(:disabled)') ||
          panelRef.current?.querySelector('button:not(:disabled)'))
      target?.focus()
      previousView.current = view
    }
  }, [view])

  const firstYear = minDate?.getFullYear() || 1
  const lastYear = maxDate?.getFullYear() || 9999
  const year = month.getFullYear()
  const monthAllowed = date => (!minDate || date >= monthAt(firstYear, minDate.getMonth())) &&
    (!maxDate || date <= monthAt(lastYear, maxDate.getMonth()))
  const canMove = direction => view === "years"
    ? direction < 0 ? yearStart > firstYear : yearStart + 9 < lastYear
    : view === "months" ? year + direction >= firstYear && year + direction <= lastYear
      : monthAllowed(monthAt(year, month.getMonth() + direction))
  const move = direction => {
    if (view === "years") setYearStart(start => Math.max(1, Math.min(9990, start + direction * 10)))
    else if (view === "months") setMonth(monthAt(year + direction, month.getMonth()))
    else setMonth(monthAt(year, month.getMonth() + direction))
  }
  const showYears = () => { setYearStart(Math.max(1, Math.min(9990, year - 9))); setView("years") }
  const arrowLabel = direction => view === "years"
    ? direction < 0 ? "10 Jahre zurück" : "10 Jahre vor"
    : view === "months" ? direction < 0 ? "Vorheriges Jahr" : "Nächstes Jahr"
      : direction < 0 ? "Vorheriger Monat" : "Nächster Monat"
  const cellClass = active => cn(
    "rounded-xl px-2 py-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--app-accent-ring)] disabled:cursor-not-allowed disabled:opacity-30",
    active ? "bg-black font-semibold text-white" : "text-[#1D1D1F] hover:bg-[#F5F5F7]"
  )

  return <div className="w-64 max-w-full">
    <div className="mb-3 flex items-center justify-between gap-1">
      <button type="button" aria-label={arrowLabel(-1)} disabled={!canMove(-1)} onClick={() => move(-1)}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#E5E5EA] hover:bg-[#F5F5F7] disabled:opacity-30">
        <ChevronLeft className="h-4 w-4" />
      </button>
      {view === "years" ? <span aria-live="polite" className="text-sm font-semibold">{yearStart}–{yearStart + 9}</span> :
        <button ref={titleRef} type="button" onClick={showYears} aria-label={view === "days" ? `${format(month, "LLLL yyyy", { locale: de })}, Jahr auswählen` : `${year}, Jahr auswählen`}
          className="rounded-lg px-3 py-2 text-sm font-semibold hover:bg-[#F5F5F7] focus-visible:ring-2 focus-visible:ring-[var(--app-accent-ring)]">
          {view === "days" ? format(month, "LLLL yyyy", { locale: de }) : year}
        </button>}
      <button type="button" aria-label={arrowLabel(1)} disabled={!canMove(1)} onClick={() => move(1)}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#E5E5EA] hover:bg-[#F5F5F7] disabled:opacity-30">
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
    {view === "days" ? <Calendar mode="single" locale={de} month={month} onMonthChange={setMonth}
      selected={selected} onSelect={onSelect} disabled={disabled} fromDate={minDate} toDate={maxDate}
      components={dayComponents} className="p-0" classNames={{ head_cell: "w-9 text-center text-xs font-normal text-muted-foreground", day: "h-9 w-9 rounded-lg p-0 text-sm font-normal hover:bg-accent aria-selected:opacity-100" }} /> :
      <div ref={panelRef} role="group" aria-label={view === "years" ? "Jahr auswählen" : "Monat auswählen"} className="grid min-h-56 grid-cols-3 content-start gap-2">
        {view === "years" ? Array.from({ length: 10 }, (_, index) => yearStart + index).map(value =>
          <button key={value} type="button" disabled={value < firstYear || value > lastYear}
            aria-pressed={value === year} className={cellClass(value === year)}
            onClick={() => { setMonth(monthAt(value, month.getMonth())); setView("months") }}>{value}</button>
        ) : Array.from({ length: 12 }, (_, index) => monthAt(year, index)).map(value =>
          <button key={value.getMonth()} type="button" disabled={!monthAllowed(value)}
            aria-pressed={value.getMonth() === month.getMonth()} className={cellClass(value.getMonth() === month.getMonth())}
            onClick={() => { setMonth(value); setView("days") }}>{format(value, "LLLL", { locale: de })}</button>
        )}
      </div>}
  </div>
}
